import { useEffect, useState } from "react";
import { useRouter } from "next/router";
import MenuLight from "@assets/icons/menuLight.svg";
import MenuDark from "@assets/icons/menuDark.svg";
import TechminisOrangeIcon from "@assets/icons/techminisOrange.svg";
import TechminisImg from "@assets/icons/techminis.svg";
import { disableScroll } from "@utils/disableScroll";
import * as clickhouse from "../../feedPage/NavigationBar/ch-events";
import ThemedImage from "@components/ui-components/ThemedImage";
import { useTheme } from "next-themes";
import dynamic from "next/dynamic";
import Link from "next/link";
import cx from "classnames";
import s from "./navigationBar.module.scss";
import WebStoriesLogo from "@assets/icons/webStoriesLogo.png";

const MenuDrawer = dynamic(() => import("@components/templates/MenuDrawer"));

type SSRNavigationBarProps = {
  headerPosition?: string;
  handleOnSignup?: () => void;
  onLogoClick?: () => void;
  showGoogleWebStories?: boolean;
  showBackButton?: boolean;
};

const SSRNavigationBar = ({
  headerPosition = "sticky",
  handleOnSignup,
  onLogoClick,
  showGoogleWebStories = true,
  showBackButton = false
}: SSRNavigationBarProps) => {
  const router = useRouter();
  const { query } = router;
  const { id } = query;
  const [menuIconClicked, setMenuIconClicked] = useState(false);
  const [openMenu, setOpenMenu] = useState(false);
  const [mounted, setMounted] = useState(false);
  const { resolvedTheme } = useTheme();
  useEffect(() => {
    setMounted(true);
  }, []);
  const onLogoClickHandler = () => {
    if (onLogoClick && typeof onLogoClick === "function") {
      onLogoClick();
    } else {
      router.push(`/?showTrendingFeed=${true}`, "/");
    }
  };

  const changeDrawerState = (isDrawerOpen: boolean, setIsDrawerOpen: Function) => () => {
    setMenuIconClicked(true);
    setIsDrawerOpen(!isDrawerOpen);
    disableScroll(!isDrawerOpen);
    clickhouse.trackCTAs({
      articleId: id,
      element_name: "hamburger_icon",
      page_section: headerPosition === "sticky" ? "sticky_header" : "floating_header"
    });
  };

  return (
    <>
      <div
        className={`items-center bg-N100 dark:bg-DN050 ${headerPosition} top-0 z-[300] flex w-full  justify-between px-4 py-4 shadow-sectionBottom shadow-[#E0E9FF] dark:shadow-[#273142]`}
      >
        <div className="flex w-full items-center justify-between">
          <div className="flex items-center">
            <ThemedImage
              mounted={mounted}
              resolvedTheme={resolvedTheme}
              darkSrc={MenuDark}
              lightSrc={MenuLight}
              alt="menu"
              onClick={changeDrawerState(openMenu, setOpenMenu)}
              width={24}
              height={24}
            />
            <div
              className="align-center ml-6 flex cursor-pointer flex-col gap-1"
              onClick={onLogoClickHandler}
            >
              <ThemedImage
                mounted={mounted}
                resolvedTheme={resolvedTheme}
                darkSrc={TechminisImg}
                lightSrc={TechminisOrangeIcon}
                width="108"
                height="18"
                alt="techminis"
              />
              <p className="ml-1 text-readingTextSmall font-regular leading-readingTextSmall tracking-wide text-N500 dark:text-N100">
                A <span className="font-bold">naukri.com</span> initiative
              </p>
            </div>
          </div>
          {showGoogleWebStories && (
            <div className="relative flex items-center">
              <div className="absolute -top-4 -left-8 rounded bg-[#F63345]">
                <div className={cx(s.arrowBottom, "relative")}>
                  <p className="px-1 py-0.5 text-s font-regular leading-s tracking-wide text-N100">
                    New
                  </p>
                </div>
              </div>
              <Link
                href={`${process.env.NEXT_PUBLIC_BASE_URL}/web-stories-categories`}
                passHref
                prefetch={false}
                // target="_blank"
              >
                <a title={"google web stories"}>
                  <ThemedImage
                    mounted={mounted}
                    resolvedTheme={resolvedTheme}
                    darkSrc={WebStoriesLogo}
                    lightSrc={WebStoriesLogo}
                    alt="google-web-stories"
                    className="h-[24px] w-[24px]"
                    onClick={() => {
                      clickhouse.trackCTAs({
                        articleId: id,
                        element_name: "google-web-stories_icon",
                        page_section:
                          headerPosition === "sticky" ? "sticky_header" : "floating_header"
                      });
                    }}
                    width={24}
                    height={24}
                  />
                </a>
              </Link>
            </div>
          )}
        </div>
        {showBackButton && (
          <Link href={`${process.env.NEXT_PUBLIC_BASE_URL}/`} passHref prefetch={false}>
            <a title="back">
              <p className="whitespace-nowrap text-readingTextSmall font-semiBold leading-readingTextSmall text-[#F94929]">
                Go Back
              </p>
            </a>
          </Link>
        )}
      </div>
      {mounted && menuIconClicked && (
        <MenuDrawer
          open={openMenu}
          onClose={() => setOpenMenu(false)}
          handleCustomizeYourFeedMenu={() => {}}
          handleOnSignup={handleOnSignup}
          menuConfig={{
            "Download the app": 1,
            "What's Trending": 1,
            "Subscribe to our newsletter": 1,
            "Customize your feed": 0,
            "Display Preferences": 1,
            "Saved for Later": 1,
            "Rate Us": 0,
            "Get In Touch": 1,
            "About Us": 1
          }}
        />
      )}
    </>
  );
};

export default SSRNavigationBar;
